import React from 'react';
import ReactDOM from 'react-dom';
import ScheduleProviderCardUtils from "./provider-card-utils.js";
import ApiUtils from "../../js/api-utils.es6.js";
import SearchApiService from "./services/search-api-service.js";
import DashboardCareTeamPage
  from "../components/dashboard-careteam/DashboardCareTeam.jsx";
import {LuminisServiceUtils} from '@js/utils/utils-imports.es6.js';
import LuminisHttpClient from "../../js/helpers/luminis-http-client.es6.js";
import {BREAKPOINTS} from "./constants.js";
import OverlayService from "@js/overlay-service.es6.js";

export default class ProviderTeam {
  constructor() {
    this.searchApiService = new SearchApiService();
    this.loadingBox = document.querySelector('.schedule-with-team-loading-box');
    this.scheduleOnlineLink = document.querySelector('.field--name-field-schedule-online-link a');
    this.callToScheduleText = document.querySelector('.field--name-field-call-button-label .field__item');
    if (this.callToScheduleText) {
      this.callToScheduleText = this.callToScheduleText.textContent;
    }
    this.outerBox = document.querySelector('.schedule-with-my-team-to-be-loaded');
    this.npi = document.querySelector('.field--name-field-provider-npi-number');
    this.scheduleWithTeamButton = document.querySelector('.schedule-with-team-button');
    this.scheduleWithTeamSection = document.querySelector('.schedule-with-my-team');
    this.currentWidth = window.innerWidth;
    this.isMobile = this.currentWidth < BREAKPOINTS.DESKTOP;
    if (this.isMobile) {
      this.scheduleWithTeamAccordion = document.querySelector('.schedule-with-my-team-accordion.mobile-only');
    } else {
      this.scheduleWithTeamAccordion = document.querySelector('.schedule-with-my-team-accordion.desktop-only');
    }

    if (!this.loadingBox || !this.scheduleOnlineLink || !this.callToScheduleText
      || !this.outerBox || !this.scheduleWithTeamButton
      || !this.scheduleWithTeamSection) {
      // we do not have our required fields
      this.hideContainerAndButton();
      return;
    }
    if (this.npi) {
      this.npi = this.npi.textContent;
    }
    this.luminisHttpClient = new LuminisHttpClient();
    this.luminisServiceUtils = new LuminisServiceUtils(
      this.luminisHttpClient,
    );
    this.overlayService = new OverlayService();
    this.mobileOverlay = document.querySelector('.right-side .mobile-overlay');
    this.mobileBackButton = document.querySelector('.schedule-with-my-team .back-button');
    this.initContainer();
    this.initSchedulingButton();
  }

  hideContainerAndButton() {
    if (this.loadingBox) {
      this.loadingBox.classList.add('hidden');
    }
    if (this.scheduleWithTeamButton) {
      this.scheduleWithTeamButton.classList.add('hidden');
    }
    if (this.scheduleWithTeamSection) {
      this.scheduleWithTeamSection.forEach(section => {
        section.classList.add('hidden');
      })
    }
  }

  async initContainer() {
    if (!this.npi) {
      this.hideContainerAndButton();
      return;
    }

    // call provider's team
    const teamJson = await this.luminisServiceUtils.getProviderTeam(this.npi);

    if (teamJson?.length > 0) {
      this.scheduleWithTeamButton.classList.remove('hidden');

      const provObjList = (
        await ApiUtils.searchProvidersByNpi(
          teamJson,
        )
      ).rows;

      if (provObjList.length > 0) {
        await this.renderProviderCards(provObjList);
      }
    } else {
      this.hideContainerAndButton();
    }

    // hide loading box
    this.loadingBox.classList.add('hidden');
  }

  initSchedulingButton() {
    if (this.isMobile) {
      this.scheduleWithTeamSection.classList.add('hidden');
    }

    this.scheduleWithTeamButton.addEventListener('click', () => {
      if (this.isMobile) {
        this.scheduleWithTeamSection.classList.remove('hidden');
        this.mobileOverlay.classList.add('active');
        this.overlayService.toggleOverlay(true, undefined, true);
        this.overlayService.desktopOverlay.addEventListener('click', () => {
          this.scheduleWithTeamSection.classList.add('hidden');
          this.mobileOverlay.classList.remove('active');
          this.overlayService.toggleOverlay(false, undefined, false);
        });
      }

      this.scheduleWithTeamSection.scrollIntoView({
        block: "start",
        behavior: "smooth"
      });
    });

    this.mobileBackButton.addEventListener('click', () => {
      if (this.isMobile) {
        this.scheduleWithTeamSection.classList.add('hidden');
        this.mobileOverlay.classList.remove('active');
        this.overlayService.toggleOverlay(false, undefined, false);
      }
    });
  }

  async renderProviderCards(providerList) {
    this.sortByLastName(providerList);
    await ScheduleProviderCardUtils.setProviderLocationInfo(providerList, null, this.searchApiService);
    const providerReactObjects = [];

    for (const provider of providerList) {
      providerReactObjects.push(await this.setupProviderFields(provider));
    }

    ReactDOM.render(
      <DashboardCareTeamPage
        careTeamProviders={providerReactObjects}
        onlineSchedulingLabel={this.scheduleOnlineLink.textContent}
        onlineSchedulingLink={this.scheduleOnlineLink.href}
        callToScheduleText={this.callToScheduleText}
        showSecondaryPhone={true}
      />,
      this.scheduleWithTeamAccordion
    );
    this.outerBox.classList.remove('hidden');
    ScheduleProviderCardUtils.updateTippy(providerList);

    // dispatch an event so that the expander adjusts when loading
    window.dispatchEvent(new Event('resize'));
  }

  sortByLastName(people) {
    return people.sort((a, b) => {
      const lastNameA = a.title_1.split(' ').pop().toLowerCase();
      const lastNameB = b.title_1.split(' ').pop().toLowerCase();

      if (lastNameA < lastNameB) {
        return -1;
      }
      if (lastNameA > lastNameB) {
        return 1;
      }
      return 0;
    });
  }

  async setupProviderFields(provider) {
    const primaryLocation = provider.primary_location;
    let hasOnlineScheduling = false;

    const mnemonicJson = await this.luminisServiceUtils.getProviderOnlineSchedulingAvailabilityInfo(
      provider.field_provider_npi_number,
      []);

    if (mnemonicJson?.visitReasonMnemonicAvailability?.length > 0) {
      hasOnlineScheduling = true;
    }
    let callButtonText = null;
    if (primaryLocation) {
     callButtonText = `${this.callToScheduleText} ${ScheduleProviderCardUtils.getLocationPhoneNumber(primaryLocation.field_phone_1)}`;
    }

    return {
      'key': provider.field_provider_npi_number,
      'name': provider.field_hero_banner_text_1,
      'specialties': ScheduleProviderCardUtils.getProviderSpecialtiesArray(provider),
      'alias': ApiUtils.buildProviderAlias(provider.title),
      'image': ScheduleProviderCardUtils.getProviderImage(provider),
      'isAffiliate': !this.searchApiService.convertBooleanStringToRealBoolean(provider.field_is_luminis_health_provider),
      'npi': provider.field_provider_npi_number,
      'ratingsLabel': provider.field_ratings_label,
      'noRatingsText': provider.field_no_rating_text,
      'noRatingsLink': provider.field_no_rating_link,
      'luminisProviderLabel': provider.field_is_luminis_health_provider_label,
      'luminisProviderTooltip': provider.field_luminis_health_provider_to,
      'affiliateProviderLabel': provider.field_affiliated_provider_label,
      'affiliateProviderTooltip': provider.field_affiliated_provider_tooltip,
      'isAcceptingTelehealth': this.searchApiService.convertBooleanStringToRealBoolean(provider.field_offering_telehealth),
      'isAcceptingTelehealthLabel': provider.field_offering_telehealth_label,
      'initialLocation': primaryLocation,
      'remainingLocations': [],
      'otherLocations': [],
      'showSchedulingOptions': false,
      'isAcceptingNewPatients': this.searchApiService.convertBooleanStringToRealBoolean(provider.field_accepting_new_patients),
      'acceptingNewPatientsLabel': provider.field_accepting_new_patients_lab,
      'hasOnlineScheduling': hasOnlineScheduling,
      'callLabel': callButtonText
    };
  }
}

(() => {
  if (document.querySelector('.node--type-provider')) {
    new ProviderTeam();
  }
})();
